import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import type { ViewerProps } from '../types'
import IgvMount, { type IgvTrack } from './IgvMount'
import { locusFromVariant, parseVariantId } from './parseVariantId'

const DEFAULT_GENOME = (import.meta.env.VITE_IGV_DEFAULT_GENOME as string | undefined) ?? 'hg38'
const DEFAULT_VCF_URL = import.meta.env.VITE_IGV_DEFAULT_VCF_URL as string | undefined
const DEFAULT_VCF_INDEX_URL = import.meta.env.VITE_IGV_DEFAULT_VCF_INDEX_URL as string | undefined

/**
 * IGV.js viewer for a single variant.
 *
 * Receives a canonical `variantId` (e.g. `chr1-12345-A-G`), derives the locus,
 * and renders the IGV browser around it. The eventual API endpoint
 * `GET /variants/{variantId}` (variant-lens-api, GEN-2886) will return a
 * signed VCF URL + locus that we will plumb through here.
 */
const IgvViewer: React.FC<ViewerProps> = ({ variantId }) => {
  const parsed = useMemo(() => parseVariantId(variantId), [variantId])
  const locus = useMemo(() => (parsed ? locusFromVariant(parsed) : undefined), [parsed])

  const tracks = useMemo<IgvTrack[]>(() => {
    if (!DEFAULT_VCF_URL) return []
    return [
      {
        name: 'Variant track',
        type: 'variant',
        format: 'vcf',
        url: DEFAULT_VCF_URL,
        indexURL: DEFAULT_VCF_INDEX_URL,
      },
    ]
  }, [])

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">IGV.js viewer</h1>
        <p className="text-sm text-gray-600">
          Variant <code className="rounded bg-gray-100 px-1.5 py-0.5">{variantId}</code>
          {parsed && (
            <>
              {' '}
              - {parsed.chrom}:{parsed.pos} {parsed.ref}&gt;{parsed.alt}
            </>
          )}
        </p>
        {!parsed && (
          <p className="text-xs text-amber-700">
            Could not parse <code>{variantId}</code> as <code>chr&lt;n&gt;-&lt;pos&gt;-&lt;ref&gt;-&lt;alt&gt;</code>;
            showing whole genome at default locus.
          </p>
        )}
        {!DEFAULT_VCF_URL && (
          <p className="text-xs text-gray-500">
            No <code>VITE_IGV_DEFAULT_VCF_URL</code> configured; rendering reference tracks only.
          </p>
        )}
        <p className="text-text-subtle text-xs">
          Need to load multiple BAM / CRAM tracks (e.g. proband + comparators)?{' '}
          <Link to="/viewers/igv/harness" className="text-brand-700 underline">
            Open the multi-track test harness
          </Link>
          .
        </p>
      </header>

      <IgvMount genome={DEFAULT_GENOME} locus={locus} tracks={tracks} />
    </section>
  )
}

export default IgvViewer
